import * as _ from 'lodash';
import { TromanError } from '../../common/TromanError';
import { certificationStates, remunerationTypes } from '../objects/Payslip';
import { claimTypes } from '../objects/Claim';

/**
 * @typedef { import('./PayslipModel').PayslipModel } PayslipModel
 * @typedef { import('./PaymentModel').PaymentModel } PaymentModel
 * @typedef { import('../objects/Payment').Payment } Payment
 * @typedef { import('../objects/Payslip').Remuneration } Remuneration
 * @typedef { import('../objects/Claim').Claim } Claim
 * @typedef { import('./calculation/CalculatedPayment').CalculatedPayment } CalculatedPayment
 * @typedef { import('./calculation/CalculatedRemuneration').CalculatedRemuneration } CalculatedRemuneration
 * @typedef { import('./calculation/PolitikerCalculator').PolitikerCalculator } PolitikerCalculator
*/

// instanceof would cause circular dependency and it would take a while to refactor
export const paymentGroupInstanceTypes = Object.freeze({
    unknown: 'unknown',
    simple: 'simple',
    complex: 'complex',
    proposedClaim: 'proposedClaim',
    proposedTime: 'proposedTime',
});

/**
 * @param {PaymentGroupModel} group
 */
function groupTypeOrder(group) {
    switch (group.paymentGroupInstanceType) {
        case paymentGroupInstanceTypes.proposedTime: return 0;
        case paymentGroupInstanceTypes.proposedClaim: return 1;
        case paymentGroupInstanceTypes.complex: return 2;
        case paymentGroupInstanceTypes.simple: return 3;
        default: return 100;
    }
}

/**
 * @param {number} remunerationType
 */
export function remunerationTypeOrder(remunerationType) {
    switch (remunerationType) {
        case remunerationTypes.claim: return 1;
        case remunerationTypes.calculatedMeeting: return 2;
        case remunerationTypes.calculatedTravel: return 3;
        case remunerationTypes.payment: return 4;
    }
}

/**
 * @param {number} claimType
 * @returns {number} in range [0,1000)
 */
function claimTypeOrder(claimType) {
    switch (claimType) {
        case claimTypes.lostIncomeAmount: return 0;
        case claimTypes.lostIncomeNumber: return 10;
        case claimTypes.lostIncomeRate: return 20;
        case claimTypes.legacyTravel: return 100;
        case claimTypes.travel: return 110;
        case claimTypes.parking: return 120;
        case claimTypes.congestion: return 130;
        case claimTypes.publicTransport: return 140;
        case claimTypes.travelTime: return 150;
        case claimTypes.expense: return 200;
        case claimTypes.adjuster: return 300;
        default: return 500 + claimType;
    }
}

/**
 * 
 * @param {PaymentModel} a 
 * @param {PaymentModel} b 
 */
export function comparePaymentModel(a, b) {
    const awt = a.payment.WageType;
    const bwt = b.payment.WageType;
    if (awt.Code < bwt.Code) return -1;
    if (bwt.Code < awt.Code) return 1;
    if (awt.Name < bwt.Name) return -1;
    if (bwt.Name < awt.Name) return 1;
    return a.Id < b.Id ? 1 : -1;
}

/**
 * 
 * @param {PaymentGroupModel} a 
 * @param {PaymentGroupModel} b 
 */
export function compareGroup(a, b) {

    const groupTypeDiff = groupTypeOrder(a) - groupTypeOrder(b);
    if (groupTypeDiff !== 0) {
        return groupTypeDiff;
    }

    const instanceDiff = a.compareTo(b);
    if (instanceDiff !== 0) {
        return instanceDiff;
    }

    if (a.hasClaim && b.hasClaim) {
        const typeDiff = claimTypeOrder(a.claim.ClaimType) - claimTypeOrder(b.claim.ClaimType);
        if (typeDiff !== 0) {
            return typeDiff;
        }
        return a.claim.Id < b.claim.Id ? -1 : 1;
    }

    if (a.isCollapsable && !b.isCollapsable) return 1;
    if (b.isCollapsable && !a.isCollapsable) return -1;

    if (a.hasCertification && !b.hasCertification) return -1;
    if (b.hasCertification && !a.hasCertification) return 1;

    if (a.payments.length === 0 && b.payments.length === 0) {
        if (a.isAdded || b.isAdded) return a.isAdded ? (b.isAdded ? 0 : -1) : 1; // should not happen
        return a.remuneration.Id < b.remuneration.Id ? -1 : 1;
    }

    if (a.payments.length === 0) return 1;
    if (b.payments.length === 0) return -1;

    let paycount = Math.min(a.payments.length, b.payments.length);

    for (let ii=0; ii<paycount; ++ii) {
        let pdiff = comparePaymentModel(a.payments[ii], b.payments[ii]);
        if (pdiff !== 0) {
            return pdiff;
        }
    }

    if (a.payments.length > b.payments.length) {
        return 1;
    }
    if (b.payments.length > a.payments.length) {
        return -1;
    }
    
    return 0;
}

export class PaymentGroupModel {
    /**
     * @param { PayslipModel } payslipModel
     */
    constructor(payslipModel) {
        this.payslipModel = payslipModel;
        
        /** @type {Remuneration|null} */
        this.remuneration = null;

        /** @type {Claim|null} */
        this.claim = null;

        /** @type {PaymentModel[]} */
        this.payments = [];

        /** @type {Set<string>} */
        this._removed = new Set();

        this.title = '';
    }

    sortPayments() {
        this.payments.sort(comparePaymentModel);
    }

    /**
     * @param {PaymentGroupModel} _other
     */
    compareTo(_other) {
        return 0;
    }
    
    // using instanceof would cause circular dependency
    get paymentGroupInstanceType() {
        return 'unknown';
    }

    get isCollapsable() {
        return false;
    }

    get hasCertification() {
        return this.remuneration != null && this.remuneration.CertificationState !== certificationStates.skipped;
    }

    get hasCompleted() {
        return this.remuneration != null && (this.remuneration.CertificationState === certificationStates.skipped || this.remuneration.CertificationState === certificationStates.certified);
    }

    get hasDetail() {
        return this.claim != null || this.hasCertification || this.remuneration?.AuditEntries.length > 0;
    }

    get certPending() {
        return this.remuneration?.CertificationState === certificationStates.pending;
    }

    get certRejected() {
        return this.remuneration?.CertificationState === certificationStates.rejected;
    }

    get certRecalled() {
        return this.remuneration?.CertificationState === certificationStates.recalled;
    }

    get certCertified() {
        return this.remuneration?.CertificationState === certificationStates.certified;
    }

    get isAdded() {
        return this.remuneration == null;
    }

    get isManuallyCalculated() {
        return false;
    }

    get hasClaim() {
        return this.claim != null;
    }

    checkChanges() {
        if (this._removed.size > 0) return true;
        return _.some(this.payments, x => x.needsSaving);
    }

    getTotalAmount() {
        return _.sumBy(this.payments, x => x.payment.Amount || 0);
    }

    _paymentsChanged() {
    }

    /**
     * @param {PaymentModel} existing
     * @param {Payment} payment
     */
    updatePayment(existing, payment) {
        if (existing.group !== this) throw new TromanError('payment_not_in_group');
        existing.payment = payment;
        existing.needsSaving = true;
        this._paymentsChanged();
    }

    /** 
     *  @param {PaymentModel} pm
     */
    removePayment(pm) {
        let pos = this.payments.indexOf(pm);
        if (pos >= 0) {
            this.payments = this.payments.slice();
            this.payments.splice(pos, 1);
            if (pm.payment.Id != null) {
                this._removed.add(pm.payment.Id);
            }
            this._paymentsChanged();
            return true;
        }
        return false;
    }

    /**
     * 
     * @param {(pm: PaymentModel)=>boolean} predicate
     */
    removeMatchingPayments(predicate) {
        let remaining = [];

        for (let pm of this.payments) {
            if (predicate(pm)) {
                if (pm.payment.Id != null){
                    this._removed.add(pm.payment.Id);
                }
            } else {
                remaining.push(pm);
            }
        }

        if (this.payments.length !== remaining.length) {
            this.payments = remaining;
            this._paymentsChanged();
            return true;
        }

        return false;
    }

    createAdd() {
        return null;
    }

    createUpdate() {
        if (this.remuneration == null) return null;

        let result = {
            Id: this.remuneration.Id,
            UpdatePayments: this.payments.filter(x => x.needsSaving).map(x => x.payment),
            RemovePayments: Array.from(this._removed),
        };
        
        return result;
    }
}
